import axios from "axios";
import { CircleX, DeleteIcon, Edit, Save } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

interface DepositBonusItem {
  deposit_no: number;
  minimum_deposit: number;
  bonus_percent: number;
}

const DepositBonus = () => {
  const [depositBonuses, setDepositBonuses] = useState<DepositBonusItem[]>([]);
  const [newBonus, setNewBonus] = useState<DepositBonusItem>({
    deposit_no: 0,
    minimum_deposit: 0,
    bonus_percent: 0,
  });
  const [editBonus, setEditBonus] = useState<DepositBonusItem | null>(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    navigate("/login");
  };

  // Fetch deposit bonuses from the API
  const fetchDepositBonuses = async () => {
    try {
      const response = await axios.get(
        "https://api.trubetx.com/admin/getDepositBonus"
      );
      if (response.data) {
        setDepositBonuses(response.data.data);
      } else {
        toast("Failed to fetch deposit bonuses.");
      }
    } catch (error) {
      console.error("Error fetching deposit bonuses:", error);
      toast("Error fetching deposit bonuses.");
    }
  };

  useEffect(() => {
    fetchDepositBonuses();
  }, []);

  // Handle input changes for new bonus
  const handleNewBonusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewBonus((prevBonus) => ({
      ...prevBonus,
      [name]: value ? parseInt(value) : 0,
    }));
  };

  // Handle input changes for editing bonus
  const handleEditBonusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editBonus) {
      const { name, value } = e.target;
      setEditBonus((prevBonus) => ({
        ...prevBonus,
        [name]: value ? parseInt(value) : 0,
      }));
    }
  };

  // Create a new deposit bonus
  const handleAddBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBonus?.minimum_deposit || !newBonus?.bonus_percent) {
      toast("Please provide valid inputs for the new bonus.");
      return;
    }

    try {
      const response = await axios.post(
        "https://api.trubetx.com/admin/createDepositBonus",
        newBonus
      );
      if (response.data.status) {
        toast("Deposit bonus added successfully!");
        setNewBonus({
          deposit_no: 0,
          minimum_deposit: 0,
          bonus_percent: 0,
        });
        fetchDepositBonuses();
      } else {
        toast(response.data.message);
      }
    } catch (error: any) {
      console.error("Error adding deposit bonus:", error);
      toast("Error adding deposit bonus.");
      toast(error.response.data.message);
    }
  };

  // Update an existing deposit bonus
  const handleUpdateBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editBonus && editBonus.minimum_deposit && editBonus.bonus_percent) {
      try {
        const response = await axios.post(
          `https://api.trubetx.com/admin/updateDepositBonus/${editBonus.deposit_no}`,
          editBonus
        );
        if (response.data) {
          toast("Deposit bonus updated successfully!");
          setEditBonus(null); // Clear the edit state
          fetchDepositBonuses();
        } else {
          toast("Failed to update deposit bonus.");
        }
      } catch (error) {
        console.error("Error updating deposit bonus:", error);
        toast("Error updating deposit bonus.");
      }
    } else {
      toast("Please provide valid inputs to update the bonus.");
    }
  };

  // Delete a deposit bonus
  const handleDeleteBonus = async (deposit_no: number) => {
    try {
      const response = await axios.delete(
        `https://api.trubetx.com/admin/deleteDepositBonus/${deposit_no}`
      );
      if (response.data.status) {
        toast("Deposit bonus deleted successfully!");
        fetchDepositBonuses();
      } else {
        toast("Failed to delete deposit bonus.");
      }
    } catch (error) {
      console.error("Error deleting deposit bonus:", error);
      toast("Error deleting deposit bonus.");
    }
  };

  return (
    <div className="ml-0 p-4 bg-white">
      <h2 className="text-2xl font-semibold mb-4 text-black">
        Add New Deposit Bonus
      </h2>
      <hr className="p-5" />

      {/* Add New Bonus Form */}
      <div className="mb-8">
        <form onSubmit={handleAddBonus} className="space-y-4">
          <div>
            <label className="text-black">Deposit No</label>
            <input
              type="number"
              name="deposit_no"
              value={newBonus.deposit_no}
              onChange={handleNewBonusChange}
              className="border rounded p-2 w-full"
            />
          </div>
          <div>
            <label className="text-black">Minimum Deposit</label>
            <input
              type="number"
              name="minimum_deposit"
              value={newBonus.minimum_deposit}
              onChange={handleNewBonusChange}
              className="border rounded p-2 w-full"
            />
          </div>
          <div>
            <label className="text-black">Bonus Percentage</label>
            <input
              type="number"
              name="bonus_percent"
              value={newBonus.bonus_percent}
              onChange={handleNewBonusChange}
              className="border rounded p-2 w-full"
            />
          </div>

          <button
    type="submit"
   
    className="flex items-center gap-2 text-sm hover:text-blue-500 hover:scale-105 transition-transform  p-2 text-gray-700"
  >
    <Save size={20} />
    <span>Add Bonus</span>
  </button>
          
        </form>
      </div>

      {/* Edit Existing Bonus Form */}
      {editBonus && (
        <div className="mb-8">
          <h3 className="text-xl text-black">Edit Deposit Bonus</h3>
          <form onSubmit={handleUpdateBonus} className="space-y-4">
            <div>
              <label className="text-black">
                Deposit No (Cannot be changed)
              </label>
              <input
                type="text"
                name="deposit_no"
                value={editBonus?.deposit_no}
                disabled
                className="border rounded p-2 w-full bg-gray-300"
              />
            </div>
            <div>
              <label className="text-black">Minimum Deposit</label>
              <input
                type="number"
                name="minimum_deposit"
                value={editBonus?.minimum_deposit}
                onChange={handleEditBonusChange}
                className="border rounded p-2 w-full"
              />
            </div>
            <div>
              <label className="text-black">Bonus Percentage</label>
              <input
                type="number"
                name="bonus_percent"
                value={editBonus?.bonus_percent}
                onChange={handleEditBonusChange}
                className="border rounded p-2 w-full"
              />
            </div>

            <div className="flex justify-start gap-10 items-center">
  <button
    type="submit"
   
    className="flex items-center gap-2 text-sm hover:text-blue-500 hover:scale-105 transition-transform"
  >
    <Save size={20} />
    <span>Update Bonus</span>
  </button>

  <button
    type="button"
    onClick={() => setEditBonus(null)}
    className="flex items-center gap-2 text-sm hover:text-red-500 transition-colors hover:scale-105 "
  >
    <CircleX size={20} />
    <span>Cancel</span>
  </button>
</div>

            
          </form>
        </div>
      )}

      {/* Display Deposit Bonuses */}
      <div>
        {depositBonuses.length > 0 ? (
          <table className="table-auto w-full text-black text-center">
            <thead>
              <tr>
                <th className="border px-4 py-2">Deposit No</th>
                <th className="border px-4 py-2">Minimum Deposit</th>
                <th className="border px-4 py-2">Bonus Percentage</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {depositBonuses?.map((bonus) => (
                <tr key={bonus.deposit_no}>
                  <td className="border px-4 py-2">
                    {bonus.deposit_no}st Deposit
                  </td>
                  <td className="border px-4 py-2">{bonus.minimum_deposit}</td>
                  <td className="border px-4 py-2">{bonus.bonus_percent}%</td>
                  <td className="border px-4 py-2 flex justify-center items-center gap-4">
                     <button
                    onClick={() => setEditBonus(bonus)}
                      className="flex items-center gap-2 text-sm hover:text-blue-500 hover:scale-105 transition-transform  p-2 text-gray-700"
                    >
                      <Edit size={20} />
                      <span> Edit</span>
                    </button>
                     <button
                    onClick={() => handleDeleteBonus(bonus.deposit_no)}
                      className="flex items-center gap-2 text-sm hover:text-red-500 hover:scale-105 transition-transform  p-2 text-gray-700"
                    >
                      <DeleteIcon size={20} />
                      <span> Delete</span>
                    </button>
                   
                   
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-black">
            No deposit bonuses available at the moment.
          </p>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default DepositBonus;
